/**
 * @fileoverview DynamoDB-backed pagination state and handlers for the company catalog.
 * @module hooks/companyCatalog/useCompanyCatalogPagination
 */
import { useCallback, useState } from "react";
import { fetchMoreCompaniesFromDynamoDB } from "../../logic/companyCatalog/index.js";
import { logger, sanitizeError } from "../../utils/logger.js";
import { canAdvanceLocalPage } from "./companyCatalogPaginationUtils.js";

/**
 * Manages current page, DynamoDB cursor, and lazy loading when the user moves
 * beyond locally cached catalog rows.
 *
 * @param {import('react-router').NavigateFunction} navigate Router navigate used on API auth failures
 * @param {number} resultsLimit Page size selected in the catalog toolbar
 * @returns {{
 *   limitReached: boolean,
 *   lastKey: string|null,
 *   currentPage: number,
 *   loading: boolean,
 *   setLastKey: import('react').Dispatch<import('react').SetStateAction<string|null>>,
 *   setLoading: import('react').Dispatch<import('react').SetStateAction<boolean>>,
 *   resetPagination: () => void,
 *   handlePageChange: (
 *     direction: 'next'|'prev',
 *     companiesData: Array<Object>,
 *     setCompaniesData: import('react').Dispatch<import('react').SetStateAction<Array<Object>>>
 *   ) => Promise<void>
 * }} Pagination state and handlers for the catalog table
 */
export const useCompanyCatalogPagination = (navigate, resultsLimit) => {
  const [limitReached, setLimitReached] = useState(false);
  const [lastKey, setLastKey] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const resetPagination = useCallback(() => {
    setCurrentPage(1);
    setLimitReached(false);
  }, []);

  const fetchAndAppendCompanies = useCallback(
    async (paginationLastKey, setCompaniesData) => {
      if (!paginationLastKey) {
        setLimitReached(true);
        return;
      }

      setLoading(true);
      try {
        const newCompanyData = await fetchMoreCompaniesFromDynamoDB(
          paginationLastKey,
          navigate,
        );
        setCompaniesData((prev) => [...prev, ...newCompanyData.items]);
        setLastKey(newCompanyData.lastKey);
        if (!newCompanyData.lastKey) {
          setLimitReached(true);
        }
        setCurrentPage((prevPage) => prevPage + 1);
      } catch (error) {
        logger.error("Error fetching more companies:", {
          err: sanitizeError(error),
        });
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  const handlePageChange = useCallback(
    async (direction, companiesData, setCompaniesData) => {
      if (direction === "next") {
        if (
          canAdvanceLocalPage(
            currentPage,
            companiesData.length,
            resultsLimit,
          )
        ) {
          setCurrentPage((prevPage) => prevPage + 1);
        } else {
          await fetchAndAppendCompanies(lastKey, setCompaniesData);
        }
        return;
      }

      if (direction === "prev" && currentPage > 1) {
        setCurrentPage((prevPage) => prevPage - 1);
        setLimitReached(false);
      }
    },
    [currentPage, fetchAndAppendCompanies, lastKey, resultsLimit],
  );

  return {
    limitReached,
    lastKey,
    currentPage,
    loading,
    setLastKey,
    setLoading,
    resetPagination,
    handlePageChange,
  };
};
